import useDispensaryPaymentMethods from 'src/hooks/use-dispensary-payment-methods';
import {
  ALL_OFFLINE_PAYMENT_METHODS,
  CASH,
  CHECK,
  DUTCHIE_PAY,
  FASTER,
  ONLINE_PAYMENT_METHODS,
  UNENROLLED_ALERT_MESSAGE,
  UNENROLLED_ALERT_MESSAGE_WITH_ONLY_PBB,
  ZERO_FEE,
} from 'src/payments/constants';
import { getOrderTypesWithDutchiePayEnabled, getPaymentFeeForMethod } from 'shared/helpers/dispensaries';

export type DispensaryPaymentOptions = NonNullable<ReturnType<typeof useDispensaryPaymentMethods>>;
type CategorizePaymentOptionResponse = {
  offlinePaymentOptions: DispensaryPaymentOptions;
  onlinePaymentOption: DispensaryPaymentOptions[number] | null;
};

export type PaymentFee = { fee: number; feeType: string; paymentType: string };

type PaymentFeeParams = {
  paymentTypes: string[];
  paymentFees: PaymentFee[];
  orderTotal: number;
};

/**
 * Picks the first online payment option from the list of payment options.
 * @param options - The payment options to pick from.
 * @returns The first online payment option from the list of payment options.
 */
export const pickOnlinePaymentOption = (options: DispensaryPaymentOptions): DispensaryPaymentOptions[number] | null => {
  if (!options.length) {
    return null;
  }
  if (options.length === 1) {
    return options[0];
  }
  const dutchiePayOption = options.find(({ value }) => value === DUTCHIE_PAY);
  return dutchiePayOption ?? options[0];
};

/**
 * Categorizes payment options into offline and online payment options.
 * @param paymentOptions - The payment options to categorize.
 * @returns An object containing the offline and online payment options.
 */
export const categorizePaymentOptions = (paymentOptions: DispensaryPaymentOptions): CategorizePaymentOptionResponse => {
  // Filter enabled payment options
  const enabledPaymentOptions = paymentOptions.filter(({ disabled }) => !disabled);

  // Filter offline payment options
  const offlinePaymentOptions = enabledPaymentOptions.filter(({ value }) =>
    ALL_OFFLINE_PAYMENT_METHODS.includes(value)
  );

  // Filter online payment options
  const onlinePaymentOptions = enabledPaymentOptions.filter(({ value }) => ONLINE_PAYMENT_METHODS.includes(value));

  const onlinePaymentOption = pickOnlinePaymentOption(onlinePaymentOptions);

  return { offlinePaymentOptions, onlinePaymentOption };
};

export const normalizePaymentFeeKeys = (paymentFees: PaymentFee[]): PaymentFee[] =>
  paymentFees.map(({ paymentType, ...fee }) => ({
    ...fee,
    paymentType: { debitOnly: 'debitCard', creditCardAtDoor: 'creditCard' }[paymentType] ?? paymentType,
  }));

export const getEnabledPayByBankOrderTypesNotSelected = ({ orderOptions, dispensary, selectedOrderType }): string[] => {
  const dutchiePayEnabledOrderTypes = getOrderTypesWithDutchiePayEnabled(dispensary);
  const availableOrderTypeLabels = dutchiePayEnabledOrderTypes
    .map((orderType) => {
      const orderOptionKey = orderTypeKeyMap[orderType];
      const matchingOrderOption = orderOptions.find((option) => option.value === orderOptionKey);
      const { label, sublabel, value } = matchingOrderOption ?? {};
      return label && value !== selectedOrderType ? `${String(label)} ${String(sublabel ?? '')}` : '';
    })
    .filter(Boolean);
  return availableOrderTypeLabels;
};

// Map of order types to the order type key in the orderOptions array
// TODO: Derive these types from their respective graphql types if available
export const orderTypeKeyMap: Record<string, string> = {
  inStorePickup: 'pickup',
  curbsidePickup: 'curbsidePickup',
  driveThruPickup: 'driveThruPickup',
  delivery: 'delivery',
};

export const getListSeparator = (currentIndex: number, totalItems: number): string => {
  if (currentIndex < totalItems - 2) {
    return ', ';
  }
  if (currentIndex === totalItems - 2) {
    return ' and ';
  }
  return ''; // No separator for the last item
};

export const getCardType = (bin: string): string => {
  const binPatterns: Record<string, RegExp> = {
    visa: /^4\d{5}/,
    mastercard: /^5[1-5]\d{4}/,
    amex: /^3[47]\d{3}/,
    discover: /^6(?:011|5\d{2})/,
  };

  return Object.keys(binPatterns).find((card) => binPatterns[card].test(bin)) ?? '';
};

const calculateFee = (fee: number, feeType: string, orderTotal: number): number =>
  feeType === 'cents' ? fee / 100 : (fee / 100) * orderTotal;

// Helper function to find the max fee for a specific payment type
const getMaxFeeForPaymentType = (paymentType: string, paymentFees: PaymentFee[], orderTotal: number): number =>
  paymentFees
    .filter((fee) => fee.paymentType === paymentType)
    .reduce((max, { fee, feeType }) => {
      const calculatedFee = calculateFee(fee, feeType, orderTotal);
      return Math.max(max, calculatedFee);
    }, 0);

const getFeeTypeForPaymentType = (paymentType: string, paymentFees: PaymentFee[]): string =>
  paymentFees.find((fee) => fee.paymentType === paymentType)?.feeType ?? '';

/**
 * Returns the fee label for the given payment type.
 * @param paymentTypes - Array of payment methods to check.
 * @param paymentFees - List of fees for each payment type with fee type (percent or cents).
 * @param orderTotal - The total amount of the order, used for percentage-based fee calculations.
 * @returns A string representing the highest fee, either as a dollar amount or percentage.
 */
export const getMaxPaymentFee = ({ paymentTypes, paymentFees, orderTotal }: PaymentFeeParams): string => {
  const maxFee = paymentTypes.reduce(
    (maxFeeType, paymentType) => {
      const feeAmount = getMaxFeeForPaymentType(paymentType, paymentFees, orderTotal);
      const feeType = getFeeTypeForPaymentType(paymentType, paymentFees);
      return feeAmount > maxFeeType.amount ? { type: paymentType, amount: feeAmount, feeType } : maxFeeType;
    },
    { type: '', amount: 0, feeType: '' }
  );

  if (!maxFee.type || !maxFee.amount) {
    const cashIsOnlyOfflineOption = paymentTypes.length === 1 && paymentTypes[0] === CASH;
    return cashIsOnlyOfflineOption ? 'Cash only' : 'Fees may apply';
  }
  const formattedAmount = formatFeeAmount(maxFee.amount);
  return `Up to $${formattedAmount} fee`;
};

export const formatFeeAmount = (amount: number): string =>
  Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);

export const getOnlinePaymentFee = (
  paymentType: string,
  paymentFees: PaymentFee[],
  disableTabFees: boolean
): string => {
  const feeInfo = getPaymentFeeForMethod(paymentType, paymentFees) || {};
  const { feeType, fee } = feeInfo;
  if (!fee || !feeType) {
    return ZERO_FEE;
  }
  if (disableTabFees) {
    return FASTER;
  }
  const usingCents = feeType === 'cents';
  return usingCents ? `$${formatFeeAmount(fee / 100)} fee` : `${String(fee)}% fee`;
};

export const getUnenrolledAlertMessage = (hasOfflinePaymentOptions: boolean): string =>
  hasOfflinePaymentOptions ? UNENROLLED_ALERT_MESSAGE : UNENROLLED_ALERT_MESSAGE_WITH_ONLY_PBB;

/**
 * Determines whether a payment method supports tipping based on dispensary settings.
 * Mirrors the tip field visibility logic in confirmation/index.jsx.
 */
export const isTippableMethod = (
  method: string,
  orderType: string,
  dispensary?: { acceptsTips?: boolean; acceptsDutchiePayTips?: boolean }
): boolean => {
  if ([CASH, CHECK].includes(method)) {
    return false;
  }
  if (method === DUTCHIE_PAY) {
    return !!dispensary?.acceptsDutchiePayTips;
  }
  return orderType === 'delivery' && !!dispensary?.acceptsTips;
};
